import { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePaymentStatus } from '@/hooks/usePaymentStatus';
import { EnhancedPricing } from '@/components/EnhancedPricing';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown } from 'lucide-react';
import { useState } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { hasAccess, loading: paymentLoading, markPaymentCompleted } = usePaymentStatus();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(true);

  // SECURITY FIX: Remove localStorage checks for authorization

  // If still loading, show loading state
  if (authLoading || paymentLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to auth
  if (!user) {
    window.location.href = '/auth';
    return null;
  }

  // SECURITY FIX: Only check server-verified access, never localStorage
  if (hasAccess) {
    return <>{children}</>;
  }

  // If user doesn't have access, show upgrade dialog or pricing page
  const handlePurchaseSuccess = () => {
    markPaymentCompleted();
    window.location.reload();
  };

  const scrollToPricing = () => {
    window.location.href = '/#pricing';
  };

  return (
    <div className="min-h-screen bg-background">
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-center justify-center">
              <Crown className="w-5 h-5 text-primary" />
              Upgrade Required
            </DialogTitle>
          </DialogHeader>
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">
              You need to upgrade to access this premium content.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={scrollToPricing} className="w-full">
                View Pricing Plans
              </Button>
              <Button variant="secondary" onClick={() => (window.location.href = '/auth')} className="w-full">
                I'm a Skool member — sign in with that email
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="w-full"
              >
                Back to Homepage
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {!showUpgradeDialog && (
        <div className="min-h-screen bg-gradient-hero">
          <EnhancedPricing onPurchaseSuccess={handlePurchaseSuccess} />
        </div>
      )}
    </div>
  );
};